import { Button } from '@heidi/design-system/button';
import { FramedIcon } from '@heidi/design-system/framed-icon';
import {
  TypographyCaption,
  TypographyFootNote,
  TypographyH2,
  TypographyH3,
  TypographyP,
  TypographyP2,
} from '@heidi/design-system/typography';
import { Clock, ShieldX, Smartphone } from 'lucide-react';
import { useEffect, useState } from 'react';
import { defineMessages, FormattedMessage } from 'react-intl';

const cibaWaitingScreenMessages = defineMessages({
  approved: {
    defaultMessage: 'Login approved',
    id: '3Nrq0VlTAB',
  },
  approvedDesc: {
    defaultMessage: 'Taking you to Heidi...',
    id: 'vSSBx1wmHK',
  },
  tryAgain: {
    defaultMessage: 'Try again',
    id: 'FazwRldA7z',
  },
  denied: {
    defaultMessage: 'Login denied',
    id: 'lsi4Oyucwa',
  },
  deniedDesc: {
    defaultMessage: 'You denied the login request.',
    id: '9keBb9wDaF',
  },
  deniedSecurity: {
    defaultMessage:
      "If you didn't request this sign-in, secure your account before trying again.",
    id: 'isZUh4KORq',
  },
  expired: {
    defaultMessage: 'Request expired',
    id: 'ONUtRE302Z',
  },
  waitingTitle: {
    defaultMessage: 'Check your phone',
    id: 'iMLA/Rt8A9',
  },
  waitingDesc: {
    defaultMessage:
      'A login request has been sent to your Heidi app. Open the notification and approve to continue.',
    id: 'gJuUTWP+qE',
  },
  chooseNumber: {
    defaultMessage: 'On your phone, choose this number',
    id: 'pN4A1nVMkt',
  },
  cancel: {
    defaultMessage: 'Cancel',
    id: '47FYwba+bI',
  },
  notEnrolledEducation: {
    defaultMessage:
      "Haven't set this up? Sign in with your password, then turn on Login with Heidi App in Settings → Login details.",
    id: 'JAzIpY369R',
    description:
      'Education copy on CIBA error/expired states telling users how to enable Login with Heidi App',
  },
});

function formatRemainingSeconds(remainingMs: number): string {
  const totalSeconds = Math.max(0, Math.ceil(remainingMs / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
}

function useRemainingMs(expiresAtMs: number | null): number | null {
  const [remainingMs, setRemainingMs] = useState<number | null>(() =>
    expiresAtMs === null ? null : Math.max(0, expiresAtMs - Date.now())
  );

  useEffect(() => {
    if (expiresAtMs === null) {
      setRemainingMs(null);
      return;
    }

    const tick = () => {
      setRemainingMs(Math.max(0, expiresAtMs - Date.now()));
    };

    tick();
    const intervalId = setInterval(tick, 1000);
    return () => clearInterval(intervalId);
  }, [expiresAtMs]);

  return remainingMs;
}

interface CibaWaitingScreenProps {
  status: 'pending' | 'approved' | 'denied' | 'expired' | 'error';
  matchCode: string | null;
  expiresAtMs: number | null;
  onCancel: () => void;
}

export function CibaWaitingScreen({
  status,
  matchCode,
  expiresAtMs,
  onCancel,
}: CibaWaitingScreenProps) {
  const remainingMs = useRemainingMs(status === 'pending' ? expiresAtMs : null);

  if (status === 'approved') {
    return (
      <div className="flex flex-col items-center gap-6 py-4 text-center">
        <FramedIcon size="lg" state="success" />
        <TypographyH3>
          <FormattedMessage {...cibaWaitingScreenMessages.approved} />
        </TypographyH3>
        <TypographyP2 className="text-text-secondary">
          <FormattedMessage {...cibaWaitingScreenMessages.approvedDesc} />
        </TypographyP2>
      </div>
    );
  }

  if (status === 'denied') {
    return (
      <div className="flex flex-col items-center gap-6 py-4 text-center">
        <FramedIcon size="lg" state="error" icon={<ShieldX />} />
        <TypographyH3>
          <FormattedMessage {...cibaWaitingScreenMessages.denied} />
        </TypographyH3>
        <TypographyP2 className="text-text-secondary">
          <FormattedMessage {...cibaWaitingScreenMessages.deniedDesc} />
        </TypographyP2>
        <TypographyFootNote className="text-text-secondary">
          <FormattedMessage {...cibaWaitingScreenMessages.deniedSecurity} />
        </TypographyFootNote>
        <Button size="lg" variant="outline" onClick={onCancel} className="w-full">
          <FormattedMessage {...cibaWaitingScreenMessages.tryAgain} />
        </Button>
      </div>
    );
  }

  if (status === 'expired') {
    return (
      <div className="flex flex-col items-center gap-6 py-4 text-center">
        <FramedIcon size="lg" state="neutral" icon={<Clock />} />
        <TypographyH3>
          <FormattedMessage {...cibaWaitingScreenMessages.expired} />
        </TypographyH3>
        <TypographyFootNote className="text-text-secondary">
          <FormattedMessage {...cibaWaitingScreenMessages.notEnrolledEducation} />
        </TypographyFootNote>
        <Button size="lg" variant="outline" onClick={onCancel} className="w-full">
          <FormattedMessage {...cibaWaitingScreenMessages.tryAgain} />
        </Button>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="flex flex-col items-center gap-6 py-4 text-center">
        <FramedIcon size="lg" state="error" />
        <TypographyH3>
          <FormattedMessage defaultMessage="Something went wrong" id="JqiqNj0Ooi" />
        </TypographyH3>
        <TypographyP2 className="text-text-secondary">
          <FormattedMessage
            defaultMessage="We could not start app approval for that email. Check the email address and try again."
            id="Vy75cca7Kp"
            description="CIBA error state body shown when the app-approval login request could not be started"
          />
        </TypographyP2>
        <TypographyFootNote className="text-text-secondary">
          <FormattedMessage {...cibaWaitingScreenMessages.notEnrolledEducation} />
        </TypographyFootNote>
        <Button size="lg" variant="outline" onClick={onCancel} className="w-full">
          <FormattedMessage {...cibaWaitingScreenMessages.tryAgain} />
        </Button>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center gap-6 py-4 text-center">
      <FramedIcon
        size="lg"
        state="neutral"
        icon={<Smartphone />}
        className="animate-pulse"
      />
      <TypographyH3>
        <FormattedMessage {...cibaWaitingScreenMessages.waitingTitle} />
      </TypographyH3>
      <TypographyP className="text-text-secondary">
        <FormattedMessage {...cibaWaitingScreenMessages.waitingDesc} />
      </TypographyP>
      <div className="flex flex-col items-center gap-1">
        <TypographyCaption className="text-text-secondary">
          <FormattedMessage {...cibaWaitingScreenMessages.chooseNumber} />
        </TypographyCaption>
        {matchCode !== null && (
          <TypographyH2
            className="rounded-lg bg-surface px-6 py-3 font-mono tracking-widest text-text-primary"
            data-testid="ciba-match-code"
          >
            {matchCode}
          </TypographyH2>
        )}
        {remainingMs !== null && (
          <TypographyCaption
            className="text-text-secondary"
            data-testid="ciba-expiry-countdown"
          >
            {formatRemainingSeconds(remainingMs)}
          </TypographyCaption>
        )}
      </div>
      <Button size="lg" variant="ghost" onClick={onCancel}>
        <FormattedMessage {...cibaWaitingScreenMessages.cancel} />
      </Button>
    </div>
  );
}
